import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { TimeBucket_TimeSeries } from '../proto/generated/common'
import { LineType } from 'lightweight-charts'

export type MultiStrikeChartTypes = 'price' | 'iv' | 'oi' | 'cumOi'

export interface MultiStrikeChartStylesTypes {
    lineThickness: number
    lineType: LineType
    autoRefresh: boolean
    notifyInterval: number
}

export type MultiStrikeSecuritiesType = Record<string, CallPutType[]>

export type MultiStrikeSecuritiesClassType = Record<string, {
    callSecKey: string
    putSecKey: string
    callPut: CallPutType[]
}>

export interface MultiStrikeChartConfig {
    key? : string
    expiry?: string
    securities?: MultiStrikeSecuritiesType
    chartType?: MultiStrikeChartTypes
    styles?: MultiStrikeChartStylesTypes
    callPut?: CallPutType[]
    cumOi?: boolean,
    nearAtm?: number | undefined
    atm?: number | undefined,
    showChange?: boolean
}

export type CallPutType = 'CE' | 'PE'

export interface OIAnalysisSettings {
    showOI: boolean
    replayKey: number
    strikeCount: string
    selectedExpiries: string[] // Array of expiry dates
    securityKey?: string // Track which security these expiries belong to
    // For OI Change (intraday vs custom range)
    rangeType?: 'intraday' | 'customRange'
    customFromDate?: number // timestamp
    customToDate?: number // timestamp
    // For Open Interest (single date picker)
    selectedDate?: number // timestamp
}

export interface OpenInterestInitialStateType {
    timeBucket: SuperChartConfiguration_TimeBucket
    securities: MultiStrikeSecuritiesType
    chartType: MultiStrikeChartTypes
    styles: MultiStrikeChartStylesTypes
    callPut: CallPutType[]
    cumOi: boolean,
    nearAtm: number | undefined
    atm: number | undefined
    loading: boolean,
    showChange: boolean,
    key : string
    oiChangeSettings: OIAnalysisSettings
    openInterestSettings: OIAnalysisSettings
}

export const homeInitialState: OpenInterestInitialStateType = {
    timeBucket: {
        code: 'Minute',
        name: '1 minute',
        timeSeries: TimeBucket_TimeSeries.MINUTE,
        timeValue: 1
    },
    key : '',
    securities: {},
    chartType: 'iv',
    styles: {
        lineThickness: 1,
        lineType: LineType.Simple,
        autoRefresh: true,
        notifyInterval: 30
    },
    callPut: ['CE', 'PE'],
    nearAtm: undefined,
    atm: undefined,
    loading: false,
    cumOi: false,
    showChange: false,
    // OI Analysis screen settings
    oiChangeSettings: {
        showOI: false,
        replayKey: 0,
        strikeCount: '25',
        selectedExpiries: [],
        securityKey: undefined,
        rangeType: 'intraday',
        customFromDate: undefined,
        customToDate: undefined
    },
    openInterestSettings: {
        showOI: false,
        replayKey: 0,
        strikeCount: '25',
        selectedExpiries: [],
        securityKey: undefined,
        selectedDate: undefined
    }
}

const multiStrikeChartSlice = createSlice({
    name: 'home',
    initialState: homeInitialState,
    reducers: {
        setMultiStrikeChartTimeBucket(state: OpenInterestInitialStateType, action: PayloadAction<SuperChartConfiguration_TimeBucket>) {
            return {
                ...state,
                timeBucket: {
                    ...action.payload
                }
            }
        },
        setMultiStrikeSecurities(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeSecuritiesType>) {
            return {
                ...state,
                securities: action.payload
            }
        },
        setMultiStrikeChartKey(state: OpenInterestInitialStateType, action: PayloadAction<string>) {
            return {
                ...state,
                key: action.payload
            }
        },
        setMultiStrikeChartType(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeChartTypes>) {
            return {
                ...state,
                chartType: action.payload
            }
        },
        setMultiStrikeCumOi(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                cumOi: action.payload
            }
        },
        setMultiStrikeShowChange(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                showChange: action.payload
            }
        },
        setMultiStrikeChartStyles(state: OpenInterestInitialStateType, action: PayloadAction<MultiStrikeChartStylesTypes>) {
            return {
                ...state,
                styles: action.payload
            }
        },
        setMultiStrikeShowCallPut(state: OpenInterestInitialStateType, action: PayloadAction<CallPutType[]>) {
            if (JSON.stringify(state.callPut) === JSON.stringify(action.payload)) {
                return state
            }
            return {
                ...state,
                callPut: action.payload
            }
        },
        setMultiStrikeNearAtm(state: OpenInterestInitialStateType, action: PayloadAction<{
            nearAtm: number | undefined
            atm: number | undefined
        }>) {
            return {
                ...state,
                nearAtm: action.payload.nearAtm,
                atm: action.payload.atm
            }
        },
        setMultiStrikeChartLoading(state: OpenInterestInitialStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                loading: action.payload
            }
        },
        // OI Change settings
        setOIChangeSettings(state: OpenInterestInitialStateType, action: PayloadAction<Partial<OIAnalysisSettings>>) {
            return {
                ...state,
                oiChangeSettings: {
                    ...state.oiChangeSettings,
                    ...action.payload
                }
            }
        },
        // Open Interest settings
        setOpenInterestSettings(state: OpenInterestInitialStateType, action: PayloadAction<Partial<OIAnalysisSettings>>) {
            return {
                ...state,
                openInterestSettings: {
                    ...state.openInterestSettings,
                    ...action.payload
                }
            }
        }
    }
})

const { actions, reducer } = multiStrikeChartSlice

export const {
    setMultiStrikeChartTimeBucket,
    setMultiStrikeSecurities,
    setMultiStrikeChartType,
    setMultiStrikeChartStyles,
    setMultiStrikeShowCallPut,
    setMultiStrikeNearAtm,
    setMultiStrikeChartLoading,
    setMultiStrikeCumOi,
    setMultiStrikeShowChange,
    setMultiStrikeChartKey,
    setOIChangeSettings,
    setOpenInterestSettings
} = actions


export default reducer
